import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useGetClientIdQuery, useGetUserDevicesQuery } from '../../state/api';
import DeviceSettings from '../../components/DeviceSettings';
import { Navigate, useParams } from 'react-router-dom';
import AssignDevice from '../../components/Device/AssignDevice';
import Chart from '../../components/Device/Chart';
import './ClientDashboard.scss';

// Get last viewed device from localStorage or default to first device (alphabetically)
const getInitialDeviceId = (devices: any[], clientId: string): string | null => {
  if (devices.length === 0) return null;
  const lastViewed = localStorage.getItem(`lastViewedDevice_${clientId}`);
  if (lastViewed && devices.some((d: any) => d._id === lastViewed)) {
    return lastViewed;
  }
  // Default to first device (alphabetically sorted)
  return devices[0]?._id || null;
};

export default function ClientDashboard() {
  const { id } = useParams();
  const userInfo = useSelector((state: any) => state.global.userInfo);
  const clientIdToFetch = id!;

  const { error } = useGetClientIdQuery(clientIdToFetch, {
    skip: !clientIdToFetch,
  });

  // Track page visibility to pause polling when tab is hidden
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Poll for device data every 5 minutes (300000 ms) to keep graphs updated
  // Only poll when the page is visible (not in background tabs)
  const { data: userDevices = [], isLoading: devicesLoading } = useGetUserDevicesQuery(
    clientIdToFetch,
    {
      pollingInterval: isPageVisible ? 5 * 60 * 1000 : 0, // 5 minutes when visible, pause when hidden
    },
  );

  // Helper function to get device name for sorting
  const getDeviceName = (device: any): string => {
    return device.settings?.name || device.location || device._id;
  };

  // Sort devices alphabetically by name
  const sortedDevices = [...userDevices].sort((a, b) => {
    const nameA = getDeviceName(a).toLowerCase();
    const nameB = getDeviceName(b).toLowerCase();
    return nameA.localeCompare(nameB, 'cs');
  });

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  // Set default device when devices load
  useEffect(() => {
    if (!devicesLoading && sortedDevices.length > 0 && selectedDeviceId === null) {
      const initialDeviceId = getInitialDeviceId(sortedDevices, clientIdToFetch);
      if (initialDeviceId) {
        setSelectedDeviceId(initialDeviceId);
        localStorage.setItem(`lastViewedDevice_${clientIdToFetch}`, initialDeviceId);
      }
    }
  }, [sortedDevices, devicesLoading, clientIdToFetch, selectedDeviceId]);

  // Save selected device to localStorage
  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    localStorage.setItem(`lastViewedDevice_${clientIdToFetch}`, deviceId);
  };

  // Filter devices based on selection
  // If no device selected yet, show the last used device (will be set by useEffect)
  const filteredDevices =
    selectedDeviceId === null
      ? sortedDevices.length > 0
        ? [sortedDevices[0]]
        : []
      : sortedDevices.filter((device: any) => device._id === selectedDeviceId);

  if (error && 'status' in error && error.status === 403) {
    return <Navigate to="/login" />;
  }
  if (userInfo === undefined) return <p>Načítám uživatele...</p>;
  if (userInfo === null) return <Navigate to="/login" />;

  return (
    <div className="client-dashboard">
      <div className="client-dashboard__left">
        <AssignDevice
          userId={clientIdToFetch}
          userDevices={sortedDevices}
          devicesLoading={devicesLoading}
        />

        {/* Device selector */}
        {sortedDevices.length > 0 && (
          <div className="device-selector">
            <label htmlFor="device-select" className="device-selector-label">
              <span className="device-icon">📱</span>
              Zařízení
            </label>
            <select
              id="device-select"
              value={selectedDeviceId || (sortedDevices.length > 0 ? sortedDevices[0]?._id : '')}
              onChange={(e) => handleDeviceChange(e.target.value)}
              className="device-select"
            >
              {sortedDevices.map((device: any) => (
                <option key={device._id} value={device._id}>
                  {device.settings?.name || device.location || `Zařízení ${device._id.slice(-6)}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Přepínač intervalu */}
        <div className="interval-select"></div>
        {filteredDevices.map((device: any) => (
          <DeviceSettings
            key={device._id}
            deviceId={device._id}
            deviceName={device.settings?.name || device.location || undefined}
            accessLevel={device.accessLevel}
          />
        ))}
      </div>

      <div className="client-dashboard__right">
        <Chart userDevices={filteredDevices} type="humidity" unit="%" title="Vlhkost" />

        <Chart
          userDevices={filteredDevices}
          type="temperature"
          unit="Teplota (°C)"
          title="Teplota"
        />
      </div>
    </div>
  );
}
